import {useMemo} from 'react';

import type {
  IShortTermHistoryGroupedRow,
  IUseShortTermHistorySubStepParams,
  IUseShortTermHistorySubStepResult,
} from '../productionInfo.types';

export function lshUseShortTermHistorySubStep({
  shortTermHistoryCropOptions,
  shortTermHistoryInitialRecords,
  shortTermHistoryRows,
}: IUseShortTermHistorySubStepParams): IUseShortTermHistorySubStepResult {
  const requiredShortTermHistoryYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 3, currentYear - 2, currentYear - 1];
  }, []);

  const shortTermHistoryCropNameById = useMemo(() => {
    const map = new Map<string, string>();

    shortTermHistoryCropOptions.forEach((option) => {
      map.set(option.id, option.name);
    });

    shortTermHistoryInitialRecords.forEach((record) => {
      const cropIdRaw = record?.crop?.id;
      if (cropIdRaw === undefined || cropIdRaw === null) return;
      const key = String(cropIdRaw);
      if (!map.has(key)) {
        map.set(key, String(record?.crop?.name || '').trim() || 'Unknown crop');
      }
    });

    return map;
  }, [shortTermHistoryCropOptions, shortTermHistoryInitialRecords]);

  const shortTermHistoryGroupedRows = useMemo(() => {
    const grouped = new Map<string, (typeof shortTermHistoryRows)[number][]>();

    shortTermHistoryRows.forEach((row) => {
      const existing = grouped.get(row.cropId) || [];
      existing.push(row);
      grouped.set(row.cropId, existing);
    });

    return Array.from(grouped.entries()).map(
      ([cropId, rows]): IShortTermHistoryGroupedRow => ({
        cropId,
        cropName: shortTermHistoryCropNameById.get(cropId) || 'Unknown crop',
        rows: [...rows].sort((left, right) => left.year - right.year),
      }),
    );
  }, [shortTermHistoryCropNameById, shortTermHistoryRows]);

  const selectedShortTermHistoryCropIds = useMemo(
    () => new Set(shortTermHistoryGroupedRows.map((group) => group.cropId)),
    [shortTermHistoryGroupedRows],
  );

  const addableShortTermHistoryCropOptions = useMemo(
    () =>
      shortTermHistoryCropOptions.filter(
        (option) => !selectedShortTermHistoryCropIds.has(option.id),
      ),
    [shortTermHistoryCropOptions, selectedShortTermHistoryCropIds],
  );

  return {
    requiredShortTermHistoryYears,
    shortTermHistoryCropNameById,
    shortTermHistoryGroupedRows,
    addableShortTermHistoryCropOptions,
  };
}
